<template>
  <div class="animated fadeIn">
    <b-card class="mt-4">
      <b-row>
        <b-col cols="2" class="pt-1">
          Bore Hole
        </b-col>
        <b-col>
          <b-form-group>
            <b-form-input
              type="text"
              v-model="bore_hole_form.identifier"
              placeholder="Enter Bore Hole Name"
              :state="!$v.bore_hole_form.identifier.$invalid"
              invalid-feedback="Required"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="2" class="pt-2">
          Field Texture
        </b-col>
        <b-col>
          <b-form-group
            :state="!$v.bore_hole_form.field_texture.$invalid"
            invalid-feedback="Required"
          >
            <form-selector
              :selectedItem="bore_hole_form.field_texture"
              :customPlaceholder="'Select Bore Hole Field Texture'"
              :options="textureOptions"
              @change="handleSelect"
            ></form-selector>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="2" class="pt-2">
          Soil Horizon
        </b-col>
        <b-col>
          <b-form-group
            :state="!$v.bore_hole_form.soil_horizon.$invalid"
            invalid-feedback="Required"
          >
            <form-selector
              :selectedItem="bore_hole_form.soil_horizon"
              :customPlaceholder="'Select Bore Hole Soil Horizon'"
              :options="horizonOptions"
              @change="handleSelect"
            ></form-selector>
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="bore_hole_update.samples && bore_hole_update.samples.length">
        <b-card>
          <irrigation-sample-form-group
            v-for="(sample, index) in bore_hole_update.samples"
            :key="index"
            :sample="sample"
            :sampleListLength="bore_hole_update.samples.length"
            @onSampleChange="onSampleChange"
            @onSampleListChange="onSampleListChange"
          ></irrigation-sample-form-group>
          <b-row align-h="end">
            <b-col cols="4">
              <b-button
                variant="success"
                block
                @click="onSampleListChange({ action: 'add' })"
                ><i class="fa fa-plus"></i> Add Sample</b-button
              >
            </b-col>
          </b-row>
        </b-card>
        <b-col v-if="boreHoleListLength > 1" class="pl-0">
          <b-button variant="danger" block @click="onRemoveClick"
            ><i class="fal fa-trash"></i> Remove Bore Hole</b-button
          >
        </b-col>
      </div>
    </b-card>
  </div>
</template>
<script>
import FormSelector from "../../FormSelector";
import IrrigationSampleFormGroup from "./IrrigationSampleFormGroup";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "IrrigationBoreHoleFormGroup",
  components: {
    FormSelector,
    IrrigationSampleFormGroup
  },
  mixins: [validationMixin, ToastNotifications],
  props: {
    bore_hole: {
      type: Object,
      default: () => {
        return {};
      }
    },
    textureOptions: {
      type: Object,
      default: () => {
        return {};
      }
    },
    horizonOptions: {
      type: Object,
      default: () => {
        return {};
      }
    },
    boreHoleListLength: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      bore_hole_form: {
        identifier: null,
        field_texture: null,
        soil_horizon: null
      },
      bore_hole_update: null
    };
  },
  validations: {
    bore_hole_form: {
      identifier: { required },
      field_texture: { required },
      soil_horizon: { required }
    }
  },
  methods: {
    handleSelect({ selected, field }) {
      this.bore_hole_form[field] = selected;
    },
    onRemoveClick() {
      this.$emit("onBoreHoleListChange", {
        action: "remove",
        bore_hole_id: this.bore_hole_update.bore_hole_id
      });
    },
    handleBoreHoleChange() {
      this.$emit("onBoreHoleChange", {
        data: this.bore_hole_update,
        bore_hole_id: this.bore_hole_update.bore_hole_id
      });
    },
    onBoreHoleFormChange() {
      const { identifier, field_texture, soil_horizon } = this.bore_hole_form;
      this.bore_hole_update = {
        ...this.bore_hole_update,
        identifier,
        field_texture,
        soil_horizon,
        samples: this.bore_hole_update.samples.map(sample => {
          return {
            ...sample,
            sample_irrigation_suitability: {
              ...sample.sample_irrigation_suitability,
              field_texture,
              soil_horizon
            }
          };
        })
      };
      this.handleBoreHoleChange();
    },
    onSampleChange({ sample_form_data, sample_id }) {
      const { depth_end, depth_start, identifier } = sample_form_data;
      this.bore_hole_update.samples = this.bore_hole_update.samples.map(
        sample => {
          if (sample.test_package_sample_id === sample_id) {
            return {
              ...sample,
              identifier,
              sample_irrigation_suitability: {
                ...sample.sample_irrigation_suitability,
                depth_start,
                depth_end
              }
            };
          }
          return sample;
        }
      );
      this.handleBoreHoleChange();
    },
    onSampleListChange({ action, sample_id }) {
      let sampleListUpdate;
      if (action === "remove") {
        sampleListUpdate = this.bore_hole_update.samples.filter(
          sample => sample.test_package_sample_id !== sample_id
        );
      } else {
        const sampleListLastItem = this.bore_hole_update.samples[
          this.bore_hole_update.samples.length - 1
        ];
        sampleListUpdate = [
          ...this.bore_hole_update.samples,
          {
            identifier: `Sample ${this.bore_hole_update.samples.length + 1}`,
            test_package_sample_id: this.$uuid.v4(),
            sample_irrigation_suitability: {
              ...sampleListLastItem.sample_irrigation_suitability,
              depth_end: (this.bore_hole_update.samples.length + 2) * 6,
              depth_start: (this.bore_hole_update.samples.length + 1) * 6
            }
          }
        ];
      }
      this.bore_hole_update.samples = sampleListUpdate.map((sample, index) => {
        return {
          ...sample,
          sample_irrigation_suitability: {
            ...sample.sample_irrigation_suitability,
            number: index + 1
          }
        };
      });
      this.handleBoreHoleChange();
    }
  },
  watch: {
    bore_hole_form: {
      handler() {
        this.onBoreHoleFormChange();
      },
      deep: true
    },
    bore_hole: {
      handler(value) {
        this.bore_hole_form.identifier = value.identifier;
        this.bore_hole_form.field_texture = value.field_texture;
        this.bore_hole_form.soil_horizon = value.soil_horizon;
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.bore_hole_update = JSON.parse(JSON.stringify(this.bore_hole));
  }
};
</script>
